import { z } from 'zod'
import { oc } from '@orpc/contract'
import {
  caqhProfileSchema,
  collectCaqhIssues,
  collectCredentialIssues,
  collectLiabilityIssues,
  collectPayerEnrollmentIssues,
  languagesSchema,
  liabilityCoverageSchema,
  memberCredentialsSchema,
  payerEnrollmentRowInputSchema,
  practitionerPhotoSchema,
} from './member-credentials.contract'

// The target's PractitionerRole is resolved server-side from the practitioner's own active
// role (see `practitioner-ops-access.ts`), never taken from the client. `organizationId`
// only picks between the roles of a target in more than one Organization.
export const updatePractitionerCredentialsInputSchema = memberCredentialsSchema
  .extend({
    practitionerId: z.string().min(1),
    organizationId: z.string().min(1).optional(),
    // Full-replace like the Credentials: present replaces the stored photo, `null` removes it.
    // Absent leaves it untouched. Not on `memberCredentialsSchema`: the invite has no photo.
    photo: practitionerPhotoSchema.nullish(),
    // The same rule for the Professional Liability Coverage, as one record: present replaces
    // the whole of it, `null` clears it, absent leaves it. Not on the invite either.
    liability: liabilityCoverageSchema.nullish(),
    // And for the CAQH Profile, as one record on the same terms.
    caqh: caqhProfileSchema.nullish(),
    // And for the Languages: present replaces the whole list, `[]` clears it, absent leaves
    // it. Not on the invite either.
    languages: languagesSchema.optional(),
    // The Active Provider status: present replaces it, absent leaves it. Not on the invite.
    activeProvider: z.boolean().optional(),
    // And for the Payer Enrollments, as one list: present replaces the whole list, `null`
    // clears it, absent leaves it. Only the Payer Enrollments tab sends it. Not on the invite,
    // and not on My Profile (`my-credentials.contract.ts`).
    payerEnrollments: z.array(payerEnrollmentRowInputSchema).nullish(),
  })
  .superRefine((data, ctx) => {
    for (const issue of [
      ...collectCredentialIssues(data),
      ...collectLiabilityIssues(data),
      ...collectCaqhIssues(data),
      ...collectPayerEnrollmentIssues(data),
    ]) {
      ctx.addIssue({ code: 'custom', ...issue })
    }
  })

export type UpdatePractitionerCredentialsInput = z.infer<
  typeof updatePractitionerCredentialsInputSchema
>

export const updatePractitionerCredentialsOutputSchema = z.object({
  practitionerId: z.string(),
  practitionerRoleId: z.string(),
})

/**
 * Writes a practitioner's Credentials (NPI, Professional Role, Specialty and state licenses),
 * photo, Professional Liability Coverage, CAQH Profile, Languages, Active Provider status and
 * Payer Enrollments.
 * Allowed for a super admin and for a Care Coordinator of the practitioner's Organization;
 * 403 otherwise.
 */
export const updatePractitionerCredentials = oc
  .route({ method: 'POST', path: '/v2/practitioners/{practitionerId}/credentials' })
  .input(updatePractitionerCredentialsInputSchema)
  .output(updatePractitionerCredentialsOutputSchema)
